import classNames from "classnames";
import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { LuCheck, LuChevronDown, LuSearch, LuUsers } from "react-icons/lu";

export type FilterMenuOption = {
  value: string;
  label: string;
  /** Status dot color. */
  dot?: string;
  /** Avatar circle (people / clients). */
  avatar?: { initials: string; color?: string; square?: boolean };
  /** Group heading (not selectable on its own look: bold). */
  isBold?: boolean;
  /** Number shown at the end of the row (e.g. works in a status). */
  count?: number;
  /** Indented under the group heading above it. */
  isSub?: boolean;
  /** Small grey text after the label (e.g. "Individual · a@b.com"). */
  detail?: string;
};

type Props = {
  placeholder: string;
  options: FilterMenuOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Purple role look with "Clear all" / "Done" (Queued tab). */
  variant?: "default" | "role";
  /** Search box instead of a button (clients). */
  search?: { value: string; onChange: (value: string) => void };
  /** Noun for "N … selected" when several are picked (clients). */
  countNoun?: string;
  minWidth?: number;
  emptyText?: string;
  /** Called when the list opens (e.g. to load its counts). */
  onOpen?: () => void;
};

const CheckBox: FC<{ checked: boolean }> = ({ checked }) =>
  checked ? (
    <span
      className={
        "inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[5px] bg-[#6B4EFF] text-white"
      }>
      <LuCheck size={11} strokeWidth={3.2} />
    </span>
  ) : (
    <span
      className={
        "box-border h-[17px] w-[17px] shrink-0 rounded-[5px] border-[1.5px] border-[#C9CDD6]"
      }
    />
  );

const Row: FC<{
  checked: boolean;
  onClick: () => void;
  children: ReactNode;
}> = ({ checked, onClick, children }) => (
  <button
    type={"button"}
    onClick={onClick}
    className={
      "flex w-full items-center gap-2.5 whitespace-nowrap rounded-[7px] px-2.5 py-2 text-left text-[12.5px] font-semibold text-[#3A4256] hover:bg-[#F5F6F8]"
    }>
    <CheckBox checked={checked} />
    {children}
  </button>
);

/** V49 filter pill with a checkbox list ("All" + options). */
export const FilterMenu: FC<Props> = ({
  placeholder,
  options,
  selected,
  onChange,
  variant = "default",
  search,
  countNoun,
  minWidth,
  emptyText = "Nothing found",
  onOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (isOpen) onOpen?.();
  }, [isOpen, onOpen]);
  const ref = useRef<HTMLDivElement>(null);
  const selectedSet = new Set(selected);
  const isRole = variant === "role";

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    // Esc closes the menu too (QA batch 12).
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  const toggle = (value: string) =>
    onChange(
      selectedSet.has(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );

  const firstLabel =
    options.find((option) => option.value === selected[0])?.label || "";
  const label = !selected.length
    ? placeholder
    : selected.length === 1
    ? firstLabel || `1 ${countNoun || "selected"}`
    : countNoun
    ? `${selected.length} ${countNoun} selected`
    : `${firstLabel || "1"} +${selected.length - 1}`;

  return (
    <div ref={ref} className={"relative"}>
      {search ? (
        <div
          className={
            "inline-flex items-center gap-[7px] whitespace-nowrap rounded-lg border-[1.5px] border-[#DCE2EA] bg-white px-3 py-2"
          }
          style={{ minWidth: minWidth ?? 170 }}>
          <LuSearch
            size={13}
            strokeWidth={2.2}
            className={"shrink-0 text-[#9097A8]"}
          />
          <input
            value={search.value}
            onChange={(event) => {
              search.onChange(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={selected.length ? label : placeholder}
            className={classNames(
              "w-full min-w-0 flex-1 border-none bg-transparent text-[13px] font-semibold outline-none",
              selected.length
                ? "placeholder:text-[#1F2433]"
                : "placeholder:text-[#9097A8]",
            )}
          />
        </div>
      ) : (
        <button
          type={"button"}
          onClick={() => setIsOpen((open) => !open)}
          className={
            isRole
              ? "inline-flex items-center gap-2 whitespace-nowrap rounded-[9px] border-[1.5px] border-[#7C66F0] bg-[#F4F2FE] px-4 py-2.5 text-sm font-bold text-[#4B2FC7] shadow-[0_1px_4px_rgba(107,78,255,0.14)]"
              : "inline-flex items-center gap-[7px] whitespace-nowrap rounded-lg border-[1.5px] border-[#DCE2EA] bg-white px-3 py-2 text-[13px] font-semibold text-[#1F2433]"
          }
          style={{ minWidth: minWidth ?? (isRole ? 210 : 150) }}>
          {isRole && (
            <LuUsers size={15} strokeWidth={2} className={"text-[#6B4EFF]"} />
          )}
          <span className={"flex-1 text-left"}>{label}</span>
          <LuChevronDown
            size={isRole ? 13 : 12}
            strokeWidth={2.4}
            className={isRole ? "text-[#6B4EFF]" : "text-[#9097A8]"}
          />
        </button>
      )}

      {isOpen && (
        <div
          className={
            "absolute left-0 top-full z-30 mt-1.5 max-h-[340px] min-w-[210px] overflow-y-auto rounded-[10px] border border-[#E5E7EB] bg-white p-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
          }>
          <Row checked={!selected.length} onClick={() => onChange([])}>
            All
          </Row>
          {options.map((option) => (
            <Row
              key={option.value}
              checked={selectedSet.has(option.value)}
              onClick={() => toggle(option.value)}>
              {option.avatar && (
                <span
                  className={classNames(
                    "inline-flex h-6 w-6 shrink-0 items-center justify-center text-[9.5px] font-bold text-white",
                    option.avatar.square ? "rounded-md" : "rounded-full",
                  )}
                  style={{ backgroundColor: option.avatar.color || "#1E3A8A" }}>
                  {option.avatar.initials}
                </span>
              )}
              {option.dot && (
                <span
                  className={classNames(
                    "h-[9px] w-[9px] shrink-0 rounded-full",
                    option.isSub && "ml-3",
                  )}
                  style={{ backgroundColor: option.dot }}
                />
              )}
              <span
                className={classNames(
                  option.isBold && "font-extrabold text-[#1F2433]",
                  option.count !== undefined && "flex-1",
                )}>
                {option.label}
                {option.detail && (
                  <span
                    className={
                      "ml-1.5 text-[11.5px] font-medium text-[#9097A8]"
                    }>
                    {option.detail}
                  </span>
                )}
              </span>
              {option.count !== undefined && (
                <span
                  className={
                    "ml-3 shrink-0 text-[12px] font-semibold tabular-nums text-[#9097A8]"
                  }>
                  {option.count}
                </span>
              )}
            </Row>
          ))}
          {!options.length && (
            <div
              className={
                "px-2.5 py-2 text-[12.5px] font-medium text-[#9097A8]"
              }>
              {emptyText}
            </div>
          )}
          {isRole && (
            <div
              className={
                "mt-[5px] flex items-center justify-between gap-2 border-t border-[#EEF0F4] px-1.5 pb-1 pt-2"
              }>
              <button
                type={"button"}
                onClick={() => onChange([])}
                className={
                  "rounded-md px-2 py-1 text-[12.5px] font-semibold text-[#6B7185] hover:bg-[#F5F6F8]"
                }>
                Clear all
              </button>
              <button
                type={"button"}
                onClick={() => setIsOpen(false)}
                className={
                  "rounded-[7px] bg-[#6B4EFF] px-4 py-1.5 text-[12.5px] font-bold text-white"
                }>
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
